import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Star,
  Heart,
  CalendarBlank,
  DotsThreeVertical,
  Clock,
  ArrowRight,
  Trash,
  PencilSimple,
  Check,
  ShareNetwork,
  Medal
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const AWARD_ICONS = {
  eat_out: { color: '#A94438', label: 'EO' },
  michelin: { color: '#C4A35A', label: 'M' },
  jhp_gourmet: { color: '#4B5945', label: 'JHP' },
  haute_grandeur: { color: '#7A7873', label: 'HG' },
};

const RestaurantCard = ({ restaurant, onDelete, onTransition, onClick, onShare }) => {
  const getStateLabel = (state) => {
    switch (state) {
      case 'want_to_try': return 'Want to Try';
      case 'planned': return 'Planned';
      case 'visited': return 'Visited';
      default: return state;
    }
  };

  const getStateClass = (state) => {
    switch (state) {
      case 'want_to_try': return 'status-want-to-try';
      case 'planned': return 'status-planned';
      case 'visited': return 'status-visited';
      default: return '';
    }
  };

  const defaultImage = 'https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div 
      className="restaurant-card card-elevated overflow-hidden cursor-pointer"
      onClick={onClick}
      data-testid={`restaurant-card-${restaurant.id}`}
    >
      {/* Image */}
      <div className="restaurant-image-container h-48 relative">
        <img
          src={restaurant.hero_image_url || defaultImage}
          alt={restaurant.name}
          className="restaurant-image w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* State Badge */}
        <div className="absolute top-3 left-3">
          <Badge className={`${getStateClass(restaurant.state)} border-0`}>
            {getStateLabel(restaurant.state)}
          </Badge>
        </div>

        {/* Favourite indicator */}
        {restaurant.flags?.favourite && (
          <div className="absolute top-3 right-3">
            <div className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Heart weight="fill" className="w-5 h-5 text-[#A94438]" />
            </div>
          </div>
        )}

        {/* Rating overlay */}
        {restaurant.aggregate_rating && (
          <div className="absolute bottom-3 right-3">
            <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1">
              <Star weight="fill" className="w-4 h-4 text-[#A94438]" />
              <span className="text-sm font-semibold">{restaurant.aggregate_rating}</span>
            </div>
          </div>
        )}

        {/* Award badges */}
        {restaurant.awards && restaurant.awards.length > 0 && (
          <div className="absolute bottom-3 left-3 flex gap-1" data-testid="card-award-badges">
            {restaurant.awards.slice(0, 3).map((award, i) => {
              const iconInfo = AWARD_ICONS[award.award_body] || AWARD_ICONS.eat_out;
              return (
                <div
                  key={i}
                  className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full px-2 py-0.5"
                  title={`${award.award_name} ${award.level} ${award.year}`}
                >
                  <Medal weight="fill" className="w-3.5 h-3.5" style={{ color: iconInfo.color }} />
                  <span className="text-[10px] font-bold" style={{ color: iconInfo.color }}>{award.level}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-xl font-semibold text-[#1A1918] line-clamp-1">
            {restaurant.name}
          </h3>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2">
                <DotsThreeVertical weight="bold" className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {restaurant.state !== 'want_to_try' && (
                <DropdownMenuItem onClick={() => onTransition('want_to_try')}>
                  Move to Want to Try
                </DropdownMenuItem>
              )}
              {restaurant.state !== 'planned' && (
                <DropdownMenuItem onClick={() => onTransition('planned')}>
                  Move to Planned
                </DropdownMenuItem>
              )}
              {restaurant.state !== 'visited' && (
                <DropdownMenuItem onClick={() => onTransition('visited')}>
                  Move to Visited
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {onShare && (
                <DropdownMenuItem onClick={onShare}>
                  <ShareNetwork className="w-4 h-4 mr-2" />
                  Share Card
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={onDelete}
                className="text-red-600"
              >
                <Trash className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Suburb */}
        {restaurant.suburb && (
          <div className="flex items-center gap-1.5 text-[#7A7873] text-sm mb-3">
            <MapPin weight="duotone" className="w-4 h-4" />
            <span>{restaurant.suburb}</span>
          </div>
        )}

        {/* Cuisine tags */}
        {restaurant.cuisine && restaurant.cuisine.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {restaurant.cuisine.slice(0, 3).map((c, i) => (
              <span key={i} className="badge-cuisine">
                {c}
              </span>
            ))}
            {restaurant.cuisine.length > 3 && (
              <span className="badge-cuisine">+{restaurant.cuisine.length - 3}</span>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm text-[#7A7873]">
          {restaurant.visit_count > 0 && (
            <div className="flex items-center gap-1">
              <Check weight="bold" className="w-4 h-4" />
              <span>{restaurant.visit_count} visit{restaurant.visit_count !== 1 ? 's' : ''}</span>
            </div>
          )}
          
          {restaurant.planned_visit_at && restaurant.state === 'planned' && (
            <div className="flex items-center gap-1">
              <CalendarBlank weight="duotone" className="w-4 h-4" />
              <span>
                {new Date(restaurant.planned_visit_at).toLocaleDateString('en-ZA', {
                  day: 'numeric',
                  month: 'short'
                })}
              </span>
            </div>
          )}

          {restaurant.price_range && (
            <span className="price-range">
              {'R'.repeat(
                restaurant.price_range === 'budget' ? 1 :
                restaurant.price_range === 'mid' ? 2 :
                restaurant.price_range === 'premium' ? 3 : 4
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantCard;
